import { trueErrorMessage } from 'utils/error'
import { videoFileHtml, videoLinkToHtml } from 'utils/video-embed'

/*
 * The "Add video" dialog: upload an MP4/WebM, or paste a YouTube / Vimeo / direct video link. It is a TinyMCE
 * dialog (not the built-in Insert/Edit Media one) so upload progress and failures show inside the popup itself.
 * TinyMCE is loaded from a CDN, so there are no bundled typings — only the slice of its API used here is typed.
 */

interface DialogApi {
	getData: () => Record<string, unknown>
	redial: (spec: unknown) => void
	close: () => void
	setEnabled: (name: string, state: boolean) => void
	showTab: (name: string) => void
}

export interface VideoDialogEditor {
	insertContent: (html: string) => void
	windowManager: { open: (spec: unknown) => unknown }
}

type DialogTab = 'upload' | 'link'

/** The link field's trimmed value; '' when it is unset or not a string. */
const readUrl = (api: DialogApi) => {
	const value = api.getData().url
	return typeof value === 'string' ? value.trim() : ''
}

const escapeText = (value: string) => value.replace(/&/g, '&amp;').replace(/</g, '&lt;')

const progressTitleHtml = (name: string, percent: number) =>
	`<p class="video-drop__title">Uploading ${escapeText(name)}</p><p class="video-drop__hint">${percent}%</p>`

// Sits where the "Choose video file" button was, so the popup keeps its height while uploading.
const progressBarHtml = (percent: number) =>
	`<div class="video-upload-slot"><div class="video-upload-bar"><div class="video-upload-bar__fill" style="width:${percent}%"></div></div></div>`

// Short: the dialog's alert banner is a single line and does not wrap.
export const LINK_ERROR = 'Use a YouTube, Vimeo or direct MP4/WebM link.'

interface OpenVideoDialogOptions {
	/** Uploads the file and resolves with its CDN URL; rejects with a user-facing message. */
	uploadVideo: (file: File, onProgress: (percent: number) => void) => Promise<string>
	maxSizeMb: number
	/** The file input's `accept` value, e.g. 'video/mp4,video/webm'. */
	accept: string
}

export function openVideoDialog(editor: VideoDialogEditor, { uploadVideo, maxSizeMb, accept }: OpenVideoDialogOptions) {
	let activeTab: DialogTab = 'upload'
	let url = ''
	let error: { tab: DialogTab; message: string } | null = null
	let closed = false
	let upload: { name: string; percent: number } | null = null

	const banner = (tab: DialogTab) =>
		error?.tab === tab ? [{ type: 'alertbanner', level: 'error', icon: 'warning', text: error.message }] : []

	const buildSpec = () => ({
		title: 'Add video',
		size: 'medium',
		body: {
			type: 'tabpanel',
			tabs: [
				{
					name: 'upload',
					title: 'Upload',
					items: upload
						? [
								{
									type: 'panel',
									classes: ['video-drop'],
									items: [
										{ type: 'htmlpanel', html: progressTitleHtml(upload.name, upload.percent) },
										{ type: 'htmlpanel', html: progressBarHtml(upload.percent) },
									],
								},
							]
						: [
								...banner('upload'),
								{
									type: 'panel',
									classes: ['video-drop'],
									items: [
										{
											type: 'htmlpanel',
											html: `<p class="video-drop__title">Drag a video here</p><p class="video-drop__hint">MP4 or WebM, up to ${maxSizeMb} MB</p>`,
										},
										// Not the built-in dropzone: its file input is hardcoded to images.
										{ type: 'button', name: 'choose', text: 'Choose video file', buttonType: 'primary' },
									],
								},
							],
				},
				{
					name: 'link',
					title: 'From link',
					items: [
						...banner('link'),
						{
							type: 'panel',
							classes: ['video-link'],
							items: [
								{
									type: 'htmlpanel',
									html: '<p class="video-link__hint">Paste a YouTube or Vimeo link, or a direct link to an MP4 or WebM file.</p>',
								},
								{ type: 'input', name: 'url', label: 'Video link', placeholder: 'https://' },
							],
						},
					],
				},
			],
		},
		buttons: [
			{ type: 'cancel', text: 'Cancel' },
			{ type: 'submit', name: 'insert', text: 'Insert', buttonType: 'primary', enabled: activeTab === 'link' && url !== '' },
		],
		initialData: { url },
		onTabChange: (api: DialogApi, details: { newTabName: string }) => {
			activeTab = details.newTabName as DialogTab
			api.setEnabled('insert', activeTab === 'link' && url !== '')
		},
		onChange: (api: DialogApi, details: { name: string }) => {
			if (details.name !== 'url') return
			url = readUrl(api)
			api.setEnabled('insert', url !== '')
		},
		onAction: (api: DialogApi, details: { name: string }) => {
			if (details.name === 'choose') chooseFile(api)
		},
		onSubmit: (api: DialogApi) => {
			url = readUrl(api)
			const html = videoLinkToHtml(url)
			if (!html) {
				error = { tab: 'link', message: LINK_ERROR }
				api.redial(buildSpec())
				// The redial resets the dialog to its first tab and disables Insert; put both back.
				api.showTab('link')
				activeTab = 'link'
				api.setEnabled('insert', true)
				return
			}
			editor.insertContent(html)
			api.close()
		},
		onClose: () => {
			closed = true
		},
	})

	// Dragging a file onto the popup uploads it, same as "Choose video file" (only while idle on the Upload tab).
	function enableDrop(dialogEl: HTMLElement, api: DialogApi) {
		const canDrop = () => activeTab === 'upload' && !upload
		dialogEl.addEventListener('dragover', event => {
			if (!canDrop()) return
			event.preventDefault()
			dialogEl.classList.add('video-dialog--dragging')
		})
		dialogEl.addEventListener('dragleave', event => {
			if (event.target === dialogEl || !dialogEl.contains(event.relatedTarget as Node | null)) {
				dialogEl.classList.remove('video-dialog--dragging')
			}
		})
		dialogEl.addEventListener('drop', event => {
			dialogEl.classList.remove('video-dialog--dragging')
			if (!canDrop()) return
			event.preventDefault()
			const file = event.dataTransfer?.files?.[0]
			if (file) void startUpload(api, file)
		})
	}

	function chooseFile(api: DialogApi) {
		const input = document.createElement('input')
		input.type = 'file'
		input.accept = accept
		input.onchange = () => {
			const file = input.files?.[0]
			if (file) void startUpload(api, file)
		}
		input.click()
	}

	async function startUpload(api: DialogApi, file: File) {
		error = null
		upload = { name: file.name, percent: 0 }
		api.redial(buildSpec())

		try {
			const cdnUrl = await uploadVideo(file, percent => {
				// Redialing rebuilds the dialog, so only do it when the bar visibly moves.
				if (closed || !upload || (percent < 100 && percent - upload.percent < 2)) return
				upload = { name: file.name, percent }
				api.redial(buildSpec())
			})
			if (closed) return
			editor.insertContent(videoFileHtml(cdnUrl, file.type))
			api.close()
		} catch (uploadError) {
			if (closed) return
			upload = null
			error = { tab: 'upload', message: trueErrorMessage(uploadError) }
			api.redial(buildSpec())
		}
	}

	const api = editor.windowManager.open(buildSpec()) as DialogApi | undefined
	// The dialog we just opened is the last one in the DOM.
	const dialogEl = Array.from(document.querySelectorAll<HTMLElement>('.tox-dialog')).pop()
	if (api && dialogEl) enableDrop(dialogEl, api)

}
